import http from '@shared/http';
import { COSMOS_V2_URL } from '@shared/const';
import type { CasesRouteSearchQuery } from '@modules/opportunities/presentation/routes';
import { UpdateOpportunityDTO } from './dto/updateOpportunity.dto';
import { CreateOpportunityDTO, CreateOpportunityResponseDTO } from './dto/createOpportunity.dto';
import { DownloadOpportunityApplicantFilesDTO } from './dto/downloadOpportunityApplicantFiles.dto';
import { OpportunityDataDTO } from './dto/getOpportunityById.dto';
import { ListOpportunitiesDTO } from './dto/listOpportunities.dto';
import { SubmitOpportunityRequestDTO } from './dto/submitOpportunity.dto';
import { getMissingBankApplicationFields, UpdateMissingBankApplicationsFields } from './dto/missingBankApplicationFields.dto';
import { Vault } from './dto/vault.dto';
import { MetaOpportunitiesDTO } from './dto/metaOpportunities.dto';

const DEFAULT_OPPORTUNITIES_PAGE_SIZE = 10;

export const opportunitiesApi = {
  getOpportunityById: (opportunityId: string) =>
    http.get<OpportunityDataDTO>(`${COSMOS_V2_URL}/opportunities/${opportunityId}`),
  listOpportunities: (clientId: string) => {
    const params = {
      client_external_ids: clientId,
      page_index: '1',
      page_size: '100',
    };
    return http.get<ListOpportunitiesDTO>(`${COSMOS_V2_URL}/opportunities/`, params);
  },
  listAllOpportunities: (query: CasesRouteSearchQuery & { page_size?: number }) => {
    const { status, submitted_by } = query;
    const statusArray = status && status.length > 0 ? status.split('|') : undefined;
    const submittedByArray = submitted_by && submitted_by.length > 0 ? submitted_by.split('|') : undefined;

    const params: {
      page_index?: number;
      page_size?: number;
      q?: string;
      status?: string[];
      referrer_external_ids?: string[];
      order?: string;
      order_by?: string;
    } = {
      page_index: query.page ?? 1,
      page_size: query.page_size ?? DEFAULT_OPPORTUNITIES_PAGE_SIZE,
      q: query.q ?? '',
      order: query.order ?? '',
      order_by: query.order_by ?? undefined,
    };

    if (statusArray) {
      params.status = statusArray;
    }

    if (submittedByArray) {
      params.referrer_external_ids = submittedByArray;
    }

    return http.get<ListOpportunitiesDTO>(`${COSMOS_V2_URL}/opportunities/`, params);
  },
  createOpportunity: async (
    body: CreateOpportunityDTO | CreateOpportunityDTO<'ES'>
  ): Promise<CreateOpportunityResponseDTO> =>
    http.post<any>(`${COSMOS_V2_URL}/opportunities/`, body),
  updateOpportunity: (opportunityId: string, body: UpdateOpportunityDTO | UpdateOpportunityDTO<'ES'>) =>
    http.patch<any>(`${COSMOS_V2_URL}/opportunities/${opportunityId}`, body),
  getMissingBankApplicationsFields: (opportunityId: string) =>
    http.get<{ bank_applications_missing_data: getMissingBankApplicationFields[] }>(
      `${COSMOS_V2_URL}/opportunities/${opportunityId}/digital-bank-applications/missing-data`
    ),
  updateMissingBankApplicationsFields: (
    opportunityId: string,
    body: UpdateMissingBankApplicationsFields
  ) =>
    http.patch<any>(
      `${COSMOS_V2_URL}/opportunities/${opportunityId}/digital-bank-applications/missing-data`,
      body
    ),
  deleteOppApplicant: (opportunityId: string, applicantId: string) =>
    http.delete<any>(`${COSMOS_V2_URL}/opportunities/${opportunityId}/applicants/${applicantId}`),
  downloadAllOpportunityDocuments: (opportunityId: string) =>
    http
      .post<DownloadOpportunityApplicantFilesDTO>(`${COSMOS_V2_URL}/opportunities/${opportunityId}/documents/download`, {}),
  downloadOpportunityApplicantDocument: (opportunityId: string, applicantId: string, documentId: string) =>
    http.get<{ url: string }>(
      `${COSMOS_V2_URL}/opportunities/${opportunityId}/applicants/${applicantId}/documents/${documentId}/presigned_url`
    ),
  downloadBankApplicationDocument: (opportunityId: string, bankApplicationId: string, documentId: string) =>
    http.get<{ url: string }>(
      `${COSMOS_V2_URL}/opportunities/${opportunityId}`
      + `/bank-application/${bankApplicationId}/documents/${documentId}/presigned_url`
    ),
  submitOpportunity: (opportunityId: string, body: SubmitOpportunityRequestDTO) =>
    http.post(`${COSMOS_V2_URL}/opportunities/${opportunityId}/submit`, body),
  uploadOpportunityApplicantDocument: (
    opportunityId: string,
    applicantId: string,
    data: FormData
  ) => http.post(
    `${COSMOS_V2_URL}/opportunities/${opportunityId}/applicants/${applicantId}/documents`,
    data
  ),
  deleteOpportunityApplicantDocument: (
    opportunityId: string,
    applicantId: string,
    externalId: string
  ) => http.delete(
    `${COSMOS_V2_URL}/opportunities/${opportunityId}/applicants/${applicantId}/documents/${externalId}`
  ),
  getVault: (opportunityId: string) => http.get<Vault>(`${COSMOS_V2_URL}/vaults/${opportunityId}`),

  getMetaOpportunities: () => http.get<MetaOpportunitiesDTO>(`${COSMOS_V2_URL}/opportunities/meta`),
  shareVaultViaEmail: (opportunityId: string) =>
    http.post<any>(`${COSMOS_V2_URL}/opportunities/${opportunityId}/share-vault-via-email`, null),
};
